/**
 * Validating a script's declaration, and resolving a trader's saved settings.
 *
 * Two jobs that look like one. Validation happens when a script is SAVED and
 * decides whether it may exist at all; resolution happens on every RUN and
 * turns whatever is in storage into the exact values handed across the Worker
 * boundary. They are separate because storage outlives validation: a script
 * edited to drop an input leaves a stale value behind in the settings of
 * everyone who installed it, and the run has to cope without failing.
 *
 * Both are total. Neither throws, and neither trusts its argument — a script
 * declaration can arrive from another trader over a share code, so `unknown` is
 * the honest input type and every field is checked before it is read.
 */
import {
  MAX_SCRIPT_ID, MAX_SCRIPT_INPUTS, MAX_SCRIPT_NAME, MAX_SCRIPT_OPTIONS,
  MAX_SCRIPT_PLOTS, MAX_SCRIPT_SOURCE_BYTES,
  SCRIPT_INPUT_KINDS, SCRIPT_PLOT_STYLES, SCRIPT_SOURCES,
  type ScriptInputDecl, type ScriptPlotDecl, type ScriptSourceName,
} from './types.js';
import { utf8Bytes } from './hash.js';

export interface ScriptDeclaration {
  readonly name: string;
  readonly overlay: boolean;
  readonly inputs: readonly ScriptInputDecl[];
  readonly plots: readonly ScriptPlotDecl[];
}

export interface ScriptValidation {
  readonly ok: boolean;
  /** Every problem found, not just the first. A form shows them all at once. */
  readonly errors: readonly string[];
  /** Present only when `ok`. Normalised — trimmed, and defaults filled in. */
  readonly declaration?: ScriptDeclaration;
}

/* An identifier that can be a property name in the script's returned object and
   a column in a legend: letters, digits and underscore, not starting with a
   digit. Deliberately narrower than JavaScript's own rules — an emoji is a
   legal identifier in a computed key and an unreadable one in a legend. */
const ID = /^[A-Za-z_][A-Za-z0-9_]*$/;

/** How many palette entries a script may index into. */
export const SCRIPT_PALETTE_SIZE = 8;

/** Widest a plotted line may be, in device-independent pixels. */
export const MAX_SCRIPT_LINE_WIDTH = 4;

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

/**
 * Check a script declaration and its source.
 *
 * `source` is checked here rather than in a separate function because its size
 * limit belongs with every other limit a save has to satisfy, and a caller that
 * validated the declaration and forgot the source would accept a 40MB script.
 */
export function validateScript(raw: unknown, source: string): ScriptValidation {
  const errors: string[] = [];

  if (!isRecord(raw)) {
    return { ok: false, errors: ['A script declaration must be an object.'] };
  }

  const name = typeof raw['name'] === 'string' ? raw['name'].trim() : '';
  if (!name) errors.push('A script needs a name.');
  else if (name.length > MAX_SCRIPT_NAME) {
    errors.push(`A name is at most ${MAX_SCRIPT_NAME} characters.`);
  }

  if (typeof raw['overlay'] !== 'boolean') {
    errors.push('overlay must be true (draw on the price pane) or false (its own pane).');
  }

  /* Bytes, not characters. The limit exists to bound what crosses a network and
     a structured clone, and a string of astral-plane characters is four times
     its own length once encoded. */
  const bytes = utf8Bytes(source).length;
  if (bytes === 0) errors.push('A script has no code in it.');
  else if (bytes > MAX_SCRIPT_SOURCE_BYTES) {
    errors.push(`A script is at most ${Math.floor(MAX_SCRIPT_SOURCE_BYTES / 1024)}KB of source.`);
  }

  const inputs = validateInputs(raw['inputs'], errors);
  const plots = validatePlots(raw['plots'], errors);

  if (errors.length > 0) return { ok: false, errors };
  return {
    ok: true,
    errors: [],
    declaration: { name, overlay: raw['overlay'] === true, inputs, plots },
  };
}

function validateInputs(raw: unknown, errors: string[]): ScriptInputDecl[] {
  if (raw === undefined) return [];
  if (!Array.isArray(raw)) {
    errors.push('inputs must be a list.');
    return [];
  }
  if (raw.length > MAX_SCRIPT_INPUTS) {
    errors.push(`A script declares at most ${MAX_SCRIPT_INPUTS} inputs.`);
    return [];
  }

  const out: ScriptInputDecl[] = [];
  const seen = new Set<string>();

  for (const [i, item] of raw.entries()) {
    const at = `input ${i + 1}`;
    if (!isRecord(item)) { errors.push(`${at} is not an object.`); continue; }

    const id = typeof item['id'] === 'string' ? item['id'] : '';
    if (!ID.test(id) || id.length > MAX_SCRIPT_ID) {
      errors.push(`${at} needs an id of letters, digits and underscore.`);
      continue;
    }
    if (seen.has(id)) { errors.push(`Two inputs are both called ${id}.`); continue; }
    seen.add(id);

    const kind = item['kind'];
    if (typeof kind !== 'string' || !(SCRIPT_INPUT_KINDS as readonly string[]).includes(kind)) {
      errors.push(`${id} has no valid kind. One of: ${SCRIPT_INPUT_KINDS.join(', ')}.`);
      continue;
    }

    const label = typeof item['label'] === 'string' && item['label'].trim()
      ? item['label'].trim() : id;

    const decl = buildInput(id, label, kind as ScriptInputDecl['kind'], item, errors);
    if (decl) out.push(decl);
  }

  return out;
}

function buildInput(
  id: string, label: string, kind: ScriptInputDecl['kind'],
  item: Record<string, unknown>, errors: string[],
): ScriptInputDecl | null {
  if (kind === 'boolean') {
    if (typeof item['def'] !== 'boolean') {
      errors.push(`${id} is a boolean and needs a true or false default.`);
      return null;
    }
    return { id, label, kind, def: item['def'] };
  }

  if (kind === 'source') {
    const def = item['def'];
    if (typeof def !== 'string' || !(SCRIPT_SOURCES as readonly string[]).includes(def)) {
      errors.push(`${id} defaults to a series that does not exist. One of: ${SCRIPT_SOURCES.join(', ')}.`);
      return null;
    }
    return { id, label, kind, def, options: [...SCRIPT_SOURCES] };
  }

  if (kind === 'select') {
    const options = item['options'];
    if (!Array.isArray(options) || options.length < 2) {
      errors.push(`${id} is a select and needs at least two options.`);
      return null;
    }
    if (options.length > MAX_SCRIPT_OPTIONS) {
      errors.push(`${id} offers more than ${MAX_SCRIPT_OPTIONS} options.`);
      return null;
    }
    if (!options.every((o) => typeof o === 'string' && o.length > 0 && o.length <= MAX_SCRIPT_ID)) {
      errors.push(`${id} has an option that is not a short string.`);
      return null;
    }
    if (new Set(options as string[]).size !== options.length) {
      errors.push(`${id} lists the same option twice.`);
      return null;
    }
    const def = item['def'];
    if (typeof def !== 'string' || !(options as string[]).includes(def)) {
      errors.push(`${id} defaults to an option it does not offer.`);
      return null;
    }
    return { id, label, kind, def, options: options as string[] };
  }

  /* number and integer. */
  const def = item['def'];
  if (typeof def !== 'number' || !Number.isFinite(def)) {
    errors.push(`${id} needs a finite numeric default.`);
    return null;
  }
  if (kind === 'integer' && !Number.isInteger(def)) {
    errors.push(`${id} is an integer and defaults to ${def}.`);
    return null;
  }

  const min = item['min'];
  const max = item['max'];
  for (const [what, v] of [['min', min], ['max', max]] as const) {
    if (v !== undefined && (typeof v !== 'number' || !Number.isFinite(v))) {
      errors.push(`${id} has a ${what} that is not a finite number.`);
      return null;
    }
    /* An integer input with a fractional bound has no good answer at run time:
       clamping to 9.5 and then rounding leaves 10, which is outside the bound,
       and rounding first and then clamping returns 9.5, which is not an
       integer. Refusing the declaration is the only outcome that cannot
       surprise the author. */
    if (kind === 'integer' && typeof v === 'number' && !Number.isInteger(v)) {
      errors.push(`${id} is an integer and has a fractional ${what}.`);
      return null;
    }
  }
  if (typeof min === 'number' && typeof max === 'number' && min > max) {
    errors.push(`${id} has a min above its max.`);
    return null;
  }
  if ((typeof min === 'number' && def < min) || (typeof max === 'number' && def > max)) {
    errors.push(`${id} defaults to a value outside its own range.`);
    return null;
  }

  return {
    id, label, kind, def,
    ...(typeof min === 'number' ? { min } : {}),
    ...(typeof max === 'number' ? { max } : {}),
  };
}

function validatePlots(raw: unknown, errors: string[]): ScriptPlotDecl[] {
  if (!Array.isArray(raw) || raw.length === 0) {
    errors.push('A script declares at least one plot. A study that draws nothing has nothing to show.');
    return [];
  }
  if (raw.length > MAX_SCRIPT_PLOTS) {
    errors.push(`A script declares at most ${MAX_SCRIPT_PLOTS} plots.`);
    return [];
  }

  const out: ScriptPlotDecl[] = [];
  const seen = new Set<string>();

  for (const [i, item] of raw.entries()) {
    const at = `plot ${i + 1}`;
    if (!isRecord(item)) { errors.push(`${at} is not an object.`); continue; }

    const id = typeof item['id'] === 'string' ? item['id'] : '';
    if (!ID.test(id) || id.length > MAX_SCRIPT_ID) {
      errors.push(`${at} needs an id of letters, digits and underscore.`);
      continue;
    }
    if (seen.has(id)) { errors.push(`Two plots are both called ${id}.`); continue; }
    seen.add(id);

    const style = item['style'];
    if (typeof style !== 'string' || !(SCRIPT_PLOT_STYLES as readonly string[]).includes(style)) {
      errors.push(`${id} has no valid style. One of: ${SCRIPT_PLOT_STYLES.join(', ')}.`);
      continue;
    }

    const colorIndex = item['colorIndex'];
    if (!Number.isInteger(colorIndex)
      || (colorIndex as number) < 0 || (colorIndex as number) >= SCRIPT_PALETTE_SIZE) {
      errors.push(`${id} picks a colour outside the palette (0 to ${SCRIPT_PALETTE_SIZE - 1}).`);
      continue;
    }

    const lineWidth = item['lineWidth'] === undefined ? 1 : item['lineWidth'];
    if (!Number.isInteger(lineWidth)
      || (lineWidth as number) < 1 || (lineWidth as number) > MAX_SCRIPT_LINE_WIDTH) {
      errors.push(`${id} has a line width outside 1 to ${MAX_SCRIPT_LINE_WIDTH}.`);
      continue;
    }

    out.push({
      id,
      label: typeof item['label'] === 'string' && item['label'].trim() ? item['label'].trim() : id,
      style: style as ScriptPlotDecl['style'],
      colorIndex: colorIndex as number,
      lineWidth: lineWidth as number,
    });
  }

  return out;
}

export type ScriptInputValue = number | boolean | string;

/**
 * Turn stored settings into the values a run receives.
 *
 * Never fails. A stored value that is missing, of the wrong type, or out of
 * range falls back to the declared default, and a stored value for an input the
 * script no longer declares is dropped. Both happen routinely: a script that is
 * edited after being installed leaves exactly this debris behind, on somebody
 * else's machine, and refusing to run is a worse answer than running as the
 * author intended.
 *
 * Clamping rather than rejecting is deliberate for numbers. `min` and `max` are
 * the author's statement of what their loop can survive, so the run has to
 * respect them whatever storage says.
 */
export function resolveScriptInputs(
  decls: readonly ScriptInputDecl[],
  stored: Readonly<Record<string, unknown>> = {},
): Record<string, ScriptInputValue> {
  const out: Record<string, ScriptInputValue> = {};

  for (const d of decls) {
    const v = stored[d.id];

    if (d.kind === 'boolean') {
      out[d.id] = typeof v === 'boolean' ? v : d.def as boolean;
      continue;
    }
    if (d.kind === 'source' || d.kind === 'select') {
      const allowed = d.options ?? [];
      out[d.id] = typeof v === 'string' && allowed.includes(v) ? v : d.def as string;
      continue;
    }

    let n = typeof v === 'number' && Number.isFinite(v) ? v : d.def as number;
    if (d.min !== undefined) n = Math.max(d.min, n);
    if (d.max !== undefined) n = Math.min(d.max, n);
    /* Order is immaterial here, and only because `validateScript` refuses a
       fractional bound on an integer input. With one, it would not be: rounding
       first can return 9.5 from an input declared as whole numbers. */
    if (d.kind === 'integer') n = Math.round(n);
    out[d.id] = n;
  }

  return out;
}

/** The series name behind a `source` input, for the payload builder. */
export function isSourceName(v: string): v is ScriptSourceName {
  return (SCRIPT_SOURCES as readonly string[]).includes(v);
}
